import type { Permission } from '../api/schema'
import { holdsPermission } from '../contexts/PermissionsContext'
import type { MemberKind } from './member-kinds'

/**
 * The shell per member kind (spec 2026-10-07-03): which sidebar entries each
 * kind gets, which `$org` sections its route guard lets through, and which
 * asset-page tabs it sees. One file, so the three are reviewed together.
 *
 * `staff` keeps the full manager menu. `resident` gets its own short list and
 * its own allow-list of sections. `provider` has an explicit stub shell that
 * behaves exactly like staff (menu, guard, asset page) until its own list
 * ships.
 *
 * An entry's permission is tested with `holdsPermission`, so the narrowed
 * `read_shared` / `read_own` / `read_assigned` codes count. The KIND decides
 * which list is used at all, which is what keeps Steering (`issues.read`)
 * away from a resident who holds `issues.read_own`.
 */

/** The sidebar entries AppSidebar knows how to draw. */
export type SidebarEntryId =
    | 'home'
    | 'search'
    | 'conversations'
    | 'assets'
    | 'contracts'
    | 'leases'
    | 'listings'
    | 'rental-applications'
    | 'assemblies'
    | 'signatures'
    | 'knowledge'
    | 'campaigns'
    | 'users'
    | 'capex'
    | 'steering'
    | 'group_steering'
    | 'benchmark'
    | 'documents'
    | 'expenses'
    | 'interventions'
    | 'imports'
    | 'activity'
    | 'agents'
    | 'issues'

export interface SidebarEntry {
    id: SidebarEntryId
    /** The `read` permission the destination needs; none = always shown. */
    permission?: Permission
    /** Require the code verbatim: a narrowed variant does not count. */
    exact?: boolean
    /** i18n key (common namespace) overriding the entry's default label. */
    labelKey?: string
    /**
     * Set on every entry of a labelled, collapsible group (a group of the menu
     * whose entries all share it): the id keys the stored collapsed state and
     * the label `nav.<group>`. Unset = an unlabelled group.
     */
    group?: string
}

/** A menu is a list of groups, each a list of entries. */
export type SidebarMenuGroups = readonly (readonly SidebarEntry[])[]

export interface KindShell {
    menu: SidebarMenuGroups
    /** Whether the user menu links to the organization settings. */
    orgSettings: boolean
    /** Whether the header offers the global search (button and command palette). */
    search: boolean
    /**
     * The `$org` sections this kind may open, as path segments under `/$org`.
     * `''` is the home. A trailing `/*` allows the sub-paths only, never the
     * segment itself: `assets/*` opens an asset page, not the asset listing.
     * Undefined = no kind restriction (the permission table still applies).
     */
    sections?: readonly string[]
    /**
     * The asset-page tabs this kind sees. Undefined = every tab the
     * permissions allow.
     */
    assetTabs?: readonly string[]
    /**
     * The asset-page tabs opened by default, first present wins. Undefined =
     * the page's own preference (photo, plan, site map).
     */
    assetDefaultTabs?: readonly string[]
    /**
     * Whether the asset page shows its manager furniture: the favourite star,
     * the graph and QR label buttons, the right rail.
     */
    assetManagerChrome: boolean
    /** Breadcrumb root of the asset page: the home as "My places", no Assets crumb. */
    myPlacesBreadcrumb: boolean
}

/** The manager menu: today's entries, in today's order. */
export const STAFF_MENU: SidebarMenuGroups = [
    [{ id: 'home' }, { id: 'search' }],
    [
        { id: 'conversations', permission: 'conversations.read' },
        { id: 'assets', permission: 'assets.read' },
        { id: 'contracts', permission: 'contracts.read' },
        { id: 'assemblies', permission: 'contracts.read' },
        { id: 'signatures', permission: 'signatures.read' },
        { id: 'users', permission: 'contacts.read' },
    ],
    [
        { id: 'capex', permission: 'capex.read' },
        // A manager console: whoever holds only `issues.read_own` follows
        // their own requests, not the portfolio's.
        { id: 'steering', permission: 'issues.read', exact: true },
        { id: 'group_steering', permission: 'group_steering.read' },
        { id: 'benchmark', permission: 'finance.read' },
    ],
    [
        { id: 'documents', permission: 'documents.read' },
        { id: 'knowledge', permission: 'knowledge.read' },
        { id: 'campaigns', permission: 'campaigns.read' },
        { id: 'expenses', permission: 'finance.read' },
        { id: 'interventions', permission: 'interventions.read' },
    ],
    [
        { id: 'leases', permission: 'contracts.read', group: 'rental_management' },
        { id: 'listings', permission: 'listings.read', group: 'rental_management' },
        { id: 'rental-applications', permission: 'rental_applications.read', group: 'rental_management' },
    ],
    [
        { id: 'imports', permission: 'data.read' },
        { id: 'agents', permission: 'data.write' },
        { id: 'activity', permission: 'organization.read' },
    ],
]

/** A resident's menu: their places, their requests, their documents, maintenance. */
export const RESIDENT_MENU: SidebarMenuGroups = [
    [
        { id: 'home', labelKey: 'nav.my_places' },
        { id: 'issues', permission: 'issues.read', labelKey: 'nav.my_requests' },
        { id: 'documents', permission: 'documents.read', labelKey: 'nav.my_documents' },
        { id: 'interventions', permission: 'interventions.read', labelKey: 'nav.maintenance' },
    ],
]

const STAFF_SHELL: KindShell = {
    menu: STAFF_MENU,
    orgSettings: true,
    search: true,
    assetManagerChrome: true,
    myPlacesBreadcrumb: false,
}

const RESIDENT_SHELL: KindShell = {
    menu: RESIDENT_MENU,
    orgSettings: false,
    search: false,
    sections: [
        '',
        'assets/*',
        'documents',
        'issues',
        'interventions',
        'user',
        // Signed-out entry points: never bounce them.
        'login',
        'password-reset',
        'sign-in-link',
    ],
    assetTabs: ['plan', 'properties', 'data', 'documents', 'interventions', 'issues'],
    // The overview first: most lots have no plan, and an empty plan is not
    // what a resident opens their home for.
    assetDefaultTabs: ['data', 'properties'],
    assetManagerChrome: false,
    myPlacesBreadcrumb: true,
}

/**
 * A provider's menu. STUB: providers keep today's manager menu, entry for
 * entry. Their own list (My sites, Interventions assigned to me, Documents)
 * comes later, with its own design; it replaces this constant then.
 */
export const PROVIDER_MENU: SidebarMenuGroups = STAFF_MENU

/**
 * A provider's shell. STUB: it behaves exactly like the staff shell (staff
 * menu, no section allow-list, every asset tab, the full manager asset page),
 * so providers see no change. The provider-specific shell comes with the
 * provider menu above.
 */
const PROVIDER_SHELL: KindShell = {
    ...STAFF_SHELL,
    menu: PROVIDER_MENU,
}

/** The shell of each kind. */
export const KIND_SHELLS: Record<MemberKind, KindShell> = {
    staff: STAFF_SHELL,
    resident: RESIDENT_SHELL,
    provider: PROVIDER_SHELL,
}

/**
 * shellForKind: the shell of a kind. An unknown kind (still loading, or no
 * membership) gets the staff shell, which is today's behaviour: the sidebar
 * never removes an entry on an unknown answer.
 */
export function shellForKind(kind: MemberKind | undefined): KindShell {
    // `kind` may be a value this build does not know yet (a newer API): staff.
    return (kind && KIND_SHELLS[kind]) || STAFF_SHELL
}

/**
 * The entries hidden unless the per-organization `lettings` flag is on: the
 * relocation flow only. Leases left the flag (spec 2026-10-27-15): the lease
 * list is the occupancy layer, not lettings.
 */
const LETTINGS_ENTRIES: readonly SidebarEntryId[] = ['listings', 'rental-applications']

/**
 * visibleMenu filters a kind's menu by permission. While the permissions are
 * not known (`known` false) nothing is filtered: the API is the enforcer, the
 * sidebar only declutters. Lettings entries need `lettings`. Empty groups are dropped.
 */
export function visibleMenu(
    kind: MemberKind | undefined,
    permissions: readonly string[],
    known: boolean,
    lettings = false
): SidebarEntry[][] {
    return shellForKind(kind)
        .menu.map((group) =>
            group.filter(
                (entry) => (lettings || !LETTINGS_ENTRIES.includes(entry.id)) && entryVisible(entry, permissions, known)
            )
        )
        .filter((group) => group.length > 0)
}

export function entryVisible(entry: SidebarEntry, permissions: readonly string[], known: boolean): boolean {
    if (!entry.permission || !known) return true
    if (entry.exact) return permissions.includes(entry.permission)

    return holdsPermission(permissions, entry.permission)
}

/**
 * sectionAllowedForKind tells the route guard whether a kind may open a path.
 * A kind with no allow-list (staff, providers for now, an unknown kind) may
 * open anything the permission table lets through.
 */
export function sectionAllowedForKind(
    kind: MemberKind | undefined,
    pathname: string,
    org: string
): boolean {
    const sections = shellForKind(kind).sections
    if (!sections) return true
    const prefix = `/${org}`
    if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return true
    const rest = pathname.slice(prefix.length).replace(/^\/+/, '').replace(/\/+$/, '')

    return sections.some((section) => {
        if (section === '') return rest === ''
        if (section.endsWith('/*')) {
            const base = section.slice(0, -2)

            return rest.startsWith(`${base}/`) && rest.length > base.length + 1
        }

        return rest === section || rest.startsWith(`${section}/`)
    })
}

/** assetTabAllowedForKind: whether a kind sees an asset-page tab. */
export function assetTabAllowedForKind(kind: MemberKind | undefined, tabId: string): boolean {
    const tabs = shellForKind(kind).assetTabs

    return !tabs || tabs.includes(tabId)
}
